import { Stack } from '@mui/material';
import { LifeInsuranceCalculatorCalculationDatagrid } from 'admin/calculators/LifeInsuranceCalculatorCalculation/LifeInsuranceCalculatorCalculationList';
import { MortgageCalculatorCalculationDatagrid } from 'admin/calculators/MortgageCalculatorCalculation/MortgageCalculatorCalculationList';
import { CashFlowAdjustmentDatagrid } from 'admin/cashFlowAdjustment/CashFlowAdjustmentDatagrid';
import { ClientNoteDatagrid } from 'admin/clientNotes/ClientNoteDatagrid';
import { FinancialAdvisorTaskDatagrid } from 'admin/financialAdvisorTask/FinancialAdvisorTaskDatagrid';
import { FinancialReserveDatagrid } from 'admin/financialReserve/FinancialReserveDatagrid';
import { ProcessDatagrid } from 'admin/processes/ProcessDatagrid';
import { ProductDatagrid } from 'admin/products/ProductDatagrid';
import { RecommendationDatagrid } from 'admin/recommendations/RecommendationDatagrid';
import { ReportDatagrid } from 'admin/reports/ReportDatagrid';
import { UserRelationSimpleDatagrid } from 'admin/userRelations/UserRelationSimpleDatagrid';
import { CALCULATORS_ENABLED, PROCESSES_ENABLED } from 'brands/salve/const';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { FilterOperator, FinancialProductType, Role, useCurrentUserQuery } from 'generated/graphql';
import { useContext } from 'react';
import {
  BooleanField,
  CreateButton,
  DateField,
  Labeled,
  ReferenceManyField,
  SelectField,
  Show,
  SimpleShowLayout,
  TabbedShowLayout,
  TextField,
  useRecordContext,
} from 'react-admin';

import { CLIENT_STATUSES } from './clientUtils';

interface ReferenceManyFieldLabelProps {
  label: string;
}

const ReferenceManyFieldLabel: React.FC<ReferenceManyFieldLabelProps> = ({ label }) => (
  <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>{label}</span>
);

interface ReferenceManyFieldButtonLabelProps {
  label: string;
  resource?: string;
  showCreateButton?: boolean;
  to?: string;
}

const ReferenceManyFieldButtonLabel: React.FC<ReferenceManyFieldButtonLabelProps> = ({
  label,
  resource,
  showCreateButton = true,
  to,
}) => {
  const client = useRecordContext();
  return (
    <Stack alignItems="center" component="label" direction="row" justifyContent="space-between">
      <ReferenceManyFieldLabel label={label} />
      {showCreateButton && (
        <CreateButton
          resource={resource}
          state={{ record: { clientId: client.id, objectUserId: client.user.id } }}
          to={to}
        />
      )}
    </Stack>
  );
};

export const ClientShow: React.FC = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { data } = useCurrentUserQuery({ skip: !isAuthenticated });

  const hasAnyRole = (allowedRoles: Role[]) =>
    isAuthenticated && data?.user?.roles?.some((role) => allowedRoles.includes(role));

  const showTasksTab = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showProductsTab = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showProcessesTab = PROCESSES_ENABLED && hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showCalculatorsTab = CALCULATORS_ENABLED && hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showRelationsTab = hasAnyRole([Role.RoleScout, Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showClientNotesTab = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showCreateRelationButton = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showReportsTab = hasAnyRole([Role.RoleScout, Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showCreateReportButton = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showFinancialReserveTab = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showCreateFinancialReserveButton = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showCashFlowAdjustmentTab = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showCreateCashFlowAdjustmentButton = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleAdmin]);
  const showRecommendationsTab = hasAnyRole([Role.RoleScout, Role.RoleFinancialAdvisor, Role.RoleAdmin]);

  return (
    <Show>
      <SimpleShowLayout>
        <TextField label="Meno" source="user.name" />
        <TextField label="Priezvisko" source="user.surname" />
        <TextField label="E-mail" source="user.email" />
        <TextField label="Tel." source="user.phoneNumber" />
        <SelectField choices={CLIENT_STATUSES} label="Stav" source="status" />
      </SimpleShowLayout>

      <TabbedShowLayout>
        {showTasksTab && (
          <TabbedShowLayout.Tab label="Úlohy">
            <ReferenceManyField
              label={<ReferenceManyFieldButtonLabel label="" resource="FinancialAdvisorTask" />}
              reference="FinancialAdvisorTask"
              target="client.id"
            >
              <FinancialAdvisorTaskDatagrid />
            </ReferenceManyField>
          </TabbedShowLayout.Tab>
        )}
        {showProcessesTab && (
          <TabbedShowLayout.Tab label="Procesy">
            <Stack spacing={4}>
              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldLabel label="Nová hypotéka" />}
                  reference="MortgagePurchaseProcess"
                  target="client.id"
                >
                  <ProcessDatagrid
                    acceptedField={<DateField label="Akceptované" locales="sk" source="acceptedTimestamp" />}
                  />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldLabel label="Refinancovanie hypotéky" />}
                  reference="MortgageRefinanceProcess"
                  target="client.id"
                >
                  <ProcessDatagrid
                    acceptedField={<DateField label="Akceptované" locales="sk" source="acceptedTimestamp" />}
                  />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldLabel label="Nové životné poistenie" />}
                  reference="LifeInsurancePurchaseProcess"
                  target="client.id"
                >
                  <ProcessDatagrid acceptedField={<BooleanField label="Akceptované" source="locked" />} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldLabel label="Audit životného poistenia" />}
                  reference="LifeInsuranceAuditProcess"
                  target="client.id"
                >
                  <ProcessDatagrid acceptedField={<BooleanField label="Akceptované" source="locked" />} />
                </ReferenceManyField>
              </Labeled>
            </Stack>
          </TabbedShowLayout.Tab>
        )}

        {showProductsTab && (
          <TabbedShowLayout.Tab label="Produkty">
            <Stack spacing={4}>
              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="Hypotéka" resource="MortgageProduct" />}
                  reference="MortgageProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.Mortgage} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="Úver" resource="LoanProduct" />}
                  reference="LoanProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.Loan} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="Kreditná karta" resource="CreditCardProduct" />}
                  reference="CreditCardProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.CreditCard} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="Životné poistenie" resource="LifeInsuranceProduct" />}
                  reference="LifeInsuranceProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.LifeInsurance} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="PZP" resource="MotorLiabilityInsuranceProduct" />}
                  reference="MotorLiabilityInsuranceProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.MotorLiabilityInsurance} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={
                    <ReferenceManyFieldButtonLabel label="Havarijné poistenie" resource="CollisionInsuranceProduct" />
                  }
                  reference="CollisionInsuranceProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.CollisionInsurance} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={
                    <ReferenceManyFieldButtonLabel label="Poistenie majetku" resource="PropertyInsuranceProduct" />
                  }
                  reference="PropertyInsuranceProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.PropertyInsurance} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="Poistenie" resource="GenericInsuranceProduct" />}
                  reference="GenericInsuranceProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.GenericInsurance} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="Investičný produkt" resource="InvestmentProduct" />}
                  reference="InvestmentProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.Investment} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={
                    <ReferenceManyFieldButtonLabel
                      label="Starobné dôchodkové sporenie"
                      resource="RetirementPensionSavingsProduct"
                    />
                  }
                  reference="RetirementPensionSavingsProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.RetirementPensionSavings} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={
                    <ReferenceManyFieldButtonLabel
                      label="Doplnkové dôchodkové sporenie"
                      resource="SupplementaryPensionSavingsProduct"
                    />
                  }
                  reference="SupplementaryPensionSavingsProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.SupplementaryPensionSavings} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="Sporenie" resource="SavingsProduct" />}
                  reference="SavingsProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.Savings} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="Stavebné sporenie" resource="BuildingSavingsProduct" />}
                  reference="BuildingSavingsProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.BuildingSavings} />
                </ReferenceManyField>
              </Labeled>

              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="Iný finančný produkt" resource="GenericProduct" />}
                  reference="GenericProduct"
                  target="client.id"
                >
                  <ProductDatagrid productType={FinancialProductType.Generic} />
                </ReferenceManyField>
              </Labeled>
            </Stack>
          </TabbedShowLayout.Tab>
        )}

        {showCalculatorsTab && (
          <TabbedShowLayout.Tab label="Kalkulačky">
            <Stack spacing={4}>
              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldLabel label="Maximálna hypotéka" />}
                  reference="MortgageCalculatorCalculation"
                  target="client.id"
                >
                  <MortgageCalculatorCalculationDatagrid />
                </ReferenceManyField>
              </Labeled>
              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldLabel label="Potreba životného poistenia" />}
                  reference="LifeInsuranceCalculatorCalculation"
                  target="client.id"
                >
                  <LifeInsuranceCalculatorCalculationDatagrid />
                </ReferenceManyField>
              </Labeled>
            </Stack>
          </TabbedShowLayout.Tab>
        )}
        {showClientNotesTab && (
          <TabbedShowLayout.Tab label="Poznámky" path="notes">
            <ReferenceManyField
              label={<ReferenceManyFieldButtonLabel label="Poznámky" resource="ClientNote" />}
              reference="ClientNote"
              target="client.id"
            >
              <ClientNoteDatagrid />
            </ReferenceManyField>
          </TabbedShowLayout.Tab>
        )}
        {showRelationsTab && (
          <TabbedShowLayout.Tab label="Vzťahy">
            <Stack spacing={4}>
              <Labeled>
                <ReferenceManyField
                  label={
                    <ReferenceManyFieldButtonLabel
                      label=""
                      resource="UserRelation"
                      showCreateButton={showCreateRelationButton}
                    />
                  }
                  reference="UserRelation"
                  sort={{ field: 'relationType', order: 'ASC' }}
                  source="user.id"
                  target="objectUser.id"
                >
                  <UserRelationSimpleDatagrid />
                </ReferenceManyField>
              </Labeled>
            </Stack>
          </TabbedShowLayout.Tab>
        )}

        {showReportsTab && (
          <TabbedShowLayout.Tab label="Výstupy">
            <Stack spacing={4}>
              <Labeled>
                <ReferenceManyField
                  label={
                    <ReferenceManyFieldButtonLabel
                      label=""
                      resource="Report"
                      showCreateButton={showCreateReportButton}
                    />
                  }
                  reference="Report"
                  sort={{ field: 'creationTimestamp', order: 'DESC' }}
                  target="client.id"
                >
                  <ReportDatagrid showClient={false} />
                </ReferenceManyField>
              </Labeled>
            </Stack>
          </TabbedShowLayout.Tab>
        )}

        {showRecommendationsTab && (
          <TabbedShowLayout.Tab label="Odporúčania">
            <Stack spacing={4}>
              <Labeled>
                <ReferenceManyField
                  label={<ReferenceManyFieldButtonLabel label="" resource="Recommendation" showCreateButton={false} />}
                  reference="Recommendation"
                  sort={{ field: 'score', order: 'ASC' }}
                  target="client.id"
                >
                  <RecommendationDatagrid />
                </ReferenceManyField>
              </Labeled>
            </Stack>
          </TabbedShowLayout.Tab>
        )}

        {showFinancialReserveTab && (
          <TabbedShowLayout.Tab label="Finančná rezerva" path="financialReserves">
            <Stack spacing={4}>
              <Labeled>
                <ReferenceManyField
                  label={
                    <ReferenceManyFieldButtonLabel
                      label=""
                      resource="FinancialReserve"
                      showCreateButton={showCreateFinancialReserveButton}
                    />
                  }
                  reference="FinancialReserve"
                  sort={{ field: 'creationTimestamp', order: 'DESC' }}
                  target="client.id"
                >
                  <FinancialReserveDatagrid />
                </ReferenceManyField>
              </Labeled>
            </Stack>
          </TabbedShowLayout.Tab>
        )}

        {showCashFlowAdjustmentTab && (
          <TabbedShowLayout.Tab label="Cash flow" path="cashFlowAdjustments">
            <Stack spacing={4}>
              <Labeled>
                <ReferenceManyField
                  filter={{
                    filterCondition: {
                      fieldPath: 'period.expectedAmount.amount',
                      operator: FilterOperator.GreaterThan,
                      values: [0],
                    },
                  }}
                  label={
                    <ReferenceManyFieldButtonLabel
                      label="Dodatočné príjmy"
                      showCreateButton={showCreateCashFlowAdjustmentButton}
                      to="/admin/CashFlowAdjustment/create?type=credit"
                    />
                  }
                  reference="CashFlowAdjustment"
                  sort={{ field: 'creationTimestamp', order: 'DESC' }}
                  target="client.id"
                >
                  <CashFlowAdjustmentDatagrid type="credit" />
                </ReferenceManyField>
              </Labeled>
              <Labeled>
                <ReferenceManyField
                  filter={{
                    filterCondition: {
                      fieldPath: 'period.expectedAmount.amount',
                      operator: FilterOperator.LessThan,
                      values: [0],
                    },
                  }}
                  label={
                    <ReferenceManyFieldButtonLabel
                      label="Dodatočné výdavky"
                      showCreateButton={showCreateCashFlowAdjustmentButton}
                      to="/admin/CashFlowAdjustment/create?type=debit"
                    />
                  }
                  reference="CashFlowAdjustment"
                  sort={{ field: 'creationTimestamp', order: 'DESC' }}
                  target="client.id"
                >
                  <CashFlowAdjustmentDatagrid type="debit" />
                </ReferenceManyField>
              </Labeled>
            </Stack>
          </TabbedShowLayout.Tab>
        )}
      </TabbedShowLayout>
    </Show>
  );
};
